/**
 * Per-org subscription helpers — single source of truth on the client.
 *
 * Storage (spec): the org owner's profile holds subscription state under
 *   user.attributes.profile.privateData.orgSubscriptions = {
 *     [orgId]: {
 *       tier,            // 'freemium' | 'starter' | 'pro' | …
 *       status,           // 'active' | 'past_due' | 'canceled' | …
 *       stripeCustomerId, stripeSubscriptionId,
 *       currentPeriodStart, currentPeriodEnd,
 *       limits: { teamSize, emailCampaigns, storageGB, ... },
 *     }
 *   }
 *
 * Private (not public) because tier/limits are billing data — only the owner
 * + the Integration API (server-side Stripe webhooks) should be able to read
 * and mutate it.
 *
 * Non-owner members never read this directly; if a member-facing screen
 * needs to know the org tier (e.g. "team size limit reached"), the server
 * surfaces it via the org-team list response or similar.
 *
 * UI helpers below intentionally read from the *current user's* private
 * data only — they're for owners checking their own org. For members, the
 * server is the gate.
 */

const SUBSCRIPTION_TIERS = Object.freeze({
  FREEMIUM: 'freemium',
  STARTER: 'starter',
  PRO: 'pro',
  ENTERPRISE: 'enterprise',
});

const SUBSCRIPTION_STATUS = Object.freeze({
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  TRIALING: 'trialing',
  INCOMPLETE: 'incomplete',
});

const DEFAULT_SUBSCRIPTION = Object.freeze({
  tier: SUBSCRIPTION_TIERS.FREEMIUM,
  status: SUBSCRIPTION_STATUS.ACTIVE,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  currentPeriodStart: null,
  currentPeriodEnd: null,
});

const getOrgSubscriptionsMap = user =>
  user?.attributes?.profile?.privateData?.orgSubscriptions || {};

/**
 * Return the subscription object for `orgId` from the user's privateData.
 * Returns null if the user is not the owner / has no record.
 */
export const getOrgSubscription = (user, orgId) => {
  if (!user || !orgId) return null;
  const map = getOrgSubscriptionsMap(user);
  return map[orgId] || null;
};

export const getOrgTier = (user, orgId) =>
  getOrgSubscription(user, orgId)?.tier || SUBSCRIPTION_TIERS.FREEMIUM;

export const isOrgSubscriptionActive = (user, orgId) => {
  const sub = getOrgSubscription(user, orgId);
  if (!sub) return true; // freemium implicit
  return (
    sub.status === SUBSCRIPTION_STATUS.ACTIVE ||
    sub.status === SUBSCRIPTION_STATUS.TRIALING
  );
};

/**
 * Build the privateData patch needed to set/update an org's subscription.
 * Caller is expected to merge with existing privateData before sending.
 */
export const buildSetOrgSubscriptionPatch = (existingPrivateData, orgId, subscription) => {
  const existingMap = existingPrivateData?.orgSubscriptions || {};
  return {
    ...existingPrivateData,
    orgSubscriptions: {
      ...existingMap,
      [orgId]: { ...DEFAULT_SUBSCRIPTION, ...subscription },
    },
  };
};

/**
 * Build the privateData patch needed to remove an org's subscription
 * (e.g. when the owner deletes the org).
 */
export const buildRemoveOrgSubscriptionPatch = (existingPrivateData, orgId) => {
  const existingMap = existingPrivateData?.orgSubscriptions || {};
  const { [orgId]: _removed, ...rest } = existingMap;
  return {
    ...existingPrivateData,
    orgSubscriptions: rest,
  };
};

export { SUBSCRIPTION_TIERS, SUBSCRIPTION_STATUS, DEFAULT_SUBSCRIPTION };
